import {
  createContext,
  FC,
  useContext,
  useEffect,
  useState,
} from "react"
import { User } from "services/auth/models/user"
import { SicalcUserRole } from "services/auth/models/sicalcUserRole"

export interface AuthService {
  currentUser: User | undefined
  /** The id of the signed in user, from the token's "sub" claim */
  currentUserId: string | undefined
  userIsAuthenticated: boolean
  userHasRole: (role: SicalcUserRole) => boolean
  currentUserRoles: SicalcUserRole[]
  setCurrentUser: (user: User) => void
  signOut: () => void
}

const localStorageKey = "Sicalc_CurrentUser"

/**
 * Read the claims in a JWT. The payload is base64url, which atob does not
 * accept as is, so convert it to plain base64 first.
 */
const getTokenClaims = (token: string) => {
  const base64 = token.split(".")[1]
    .replace(/-/g, "+")
    .replace(/_/g, "/")
  return JSON.parse(window.atob(base64))
}

const AuthServiceContext = createContext<AuthService | undefined>(undefined)

export const AuthServiceProvider: FC = ({ children }) => {
  const localStorageUserString = localStorage.getItem(localStorageKey)
  const localStorageUser = localStorageUserString
    ? JSON.parse(localStorageUserString) as User
    : undefined

  const [ currentUser, setCurrentUser ] = useState<User | undefined>(localStorageUser)

  // Give the isAuthenticated property a nice getter, which
  // can't be done directly inside a functional component
  const userIsAuthenticatedGetterWrapper = {
    get userIsAuthenticated() {
      return currentUser !== undefined
    },
  }

  // Give the currentUserRoles property a nice getter, which
  // can't be done directly inside a functional component
  const currentUserRolesGetterWrapper = {
    get currentUserRoles() {
      const token = currentUser?.token
      if (!token) {
        return []
      }

      const tokenClaims = getTokenClaims(token)

      if (Array.isArray(tokenClaims.role)) {
        return tokenClaims.role
      }

      if (typeof tokenClaims.role !== 'string') {
        return []
      }

      return [tokenClaims.role]
    },
  }

  const currentUserId: string | undefined = currentUser?.token
    ? getTokenClaims(currentUser.token).sub
    : undefined

  const signOut = () => {
    setCurrentUser(undefined)
  }

  const userHasRole = (role: SicalcUserRole) => {
    const token = currentUser?.token
    if (!token) {
      return false
    }

    const tokenClaims = getTokenClaims(token)

    if (Array.isArray(tokenClaims.role)) {
      return tokenClaims.role.includes(role)
    } else {
      return tokenClaims.role === role
    }
  }

  useEffect(() => {
    if (currentUser !== undefined) {
      localStorage.setItem(localStorageKey, JSON.stringify(currentUser))
    } else {
      localStorage.removeItem(localStorageKey)
    }
  }, [ currentUser ])

  return (
    <AuthServiceContext.Provider
      value={{
        currentUser,
        currentUserId,
        userIsAuthenticated: userIsAuthenticatedGetterWrapper.userIsAuthenticated,
        currentUserRoles: currentUserRolesGetterWrapper.currentUserRoles,
        userHasRole,
        setCurrentUser,
        signOut,
      }}
    >
      {children}
    </AuthServiceContext.Provider>
  )
}

export const useAuthService = () => {
  const context = useContext<AuthService | undefined>(AuthServiceContext)
  if (!context) {
    const serviceName = Object.keys({ AuthServiceContext })[0]
    throw new Error(serviceName + " was not provided. "
      + "Make sure the component is a child of the required service provider")
  }
  return context
}
